// Delivery estimate block: business-day window computed in the browser from merchant settings (no invented data).
// Dates use the store's UTC offset rendered by Liquid, so "today", the cutoff and weekends follow store time.
// Hidden until computed; hidden for sold-out and pre-order variants (it never promises dates for those).

const DAY = 86400000;

export function parseOffset(value) {
  const match = /^([+-])(\d{2}):?(\d{2})$/.exec(String(value || '').trim());
  if (!match) return 0;
  const minutes = Number(match[2]) * 60 + Number(match[3]);
  return match[1] === '-' ? -minutes : minutes;
}

const isoDay = (wall) => wall.toISOString().slice(0, 10);

// `now` is a real instant; all arithmetic happens on the store's wall clock expressed in UTC fields.
export function estimateDelivery({ now = Date.now(), offsetMinutes = 0, min = 3, max = 7, cutoffHour = 24, excluded = new Set(), holidays = new Set() }) {
  const low = Math.max(0, Math.min(min, max));
  const high = Math.max(min, max);
  const wallNow = new Date(now + offsetMinutes * 60000);
  const startOfToday = Date.UTC(wallNow.getUTCFullYear(), wallNow.getUTCMonth(), wallNow.getUTCDate());
  const isBusiness = (time) => { const date = new Date(time); return !excluded.has(date.getUTCDay()) && !holidays.has(isoDay(date)); };
  const cutoff = startOfToday + cutoffHour * 3600000;
  let dispatch = startOfToday;
  const shipsToday = isBusiness(startOfToday) && wallNow.getTime() < cutoff;
  if (!shipsToday) {
    dispatch += DAY;
    while (!isBusiness(dispatch)) dispatch += DAY;
  }
  const addBusinessDays = (from, count) => {
    let cursor = from;
    for (let added = 0; added < count;) { cursor += DAY; if (isBusiness(cursor)) added += 1; }
    return cursor;
  };
  return {
    dispatch: new Date(dispatch),
    start: new Date(addBusinessDays(dispatch, low)),
    end: new Date(addBusinessDays(dispatch, high)),
    shipsToday,
    remainingMs: shipsToday ? cutoff - wallNow.getTime() : 0
  };
}

// No-break spaces keep each date on one line (Arabic phones broke "14" from its month).
export function joinDate(text) {
  return text.replace(/\s+/g, '\u00a0');
}

export function formatRemaining(ms, locale) {
  const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const unit = (value, name) => new Intl.NumberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'short' }).format(value);
  if (hours === 0) return unit(minutes, 'minute');
  return minutes === 0 ? unit(hours, 'hour') : `${unit(hours, 'hour')} ${unit(minutes, 'minute')}`;
}

function mount(root) {
  if (root.dataset.deliveryMounted) return;
  root.dataset.deliveryMounted = 'true';
  const data = root.dataset;
  const locale = `${data.locale || document.documentElement.lang || 'en'}-u-nu-latn`;
  const dateFormat = new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
  const settings = {
    offsetMinutes: parseOffset(data.offset),
    min: Number(data.min || 0),
    max: Number(data.max || 0),
    cutoffHour: Number(data.cutoff ?? 24),
    excluded: new Set((data.excluded || '').split(',').filter(Boolean).map(Number)),
    holidays: new Set((data.holidays || '').split(',').map((item) => item.trim()).filter((item) => /^\d{4}-\d{2}-\d{2}$/.test(item)))
  };
  const product = root.closest('[data-product-page]');
  const text = root.querySelector('[data-delivery-text]');
  const cutoffNode = root.querySelector('[data-delivery-cutoff]');
  let purchasable = true;

  const readState = () => {
    const state = product?.querySelector('[data-product-state]')?.dataset;
    purchasable = !state || (state.available === 'true' && state.preorder !== 'true');
  };
  const render = () => {
    if (!purchasable) { root.hidden = true; return; }
    const result = estimateDelivery({ ...settings, now: Date.now() });
    const start = joinDate(dateFormat.format(result.start));
    const end = joinDate(dateFormat.format(result.end));
    text.textContent = start === end ? data.templateSingle.replace('DATE_TOKEN', start) : data.templateRange.replace('START_TOKEN', start).replace('END_TOKEN', end);
    if (cutoffNode) {
      const show = data.showCutoff === 'true' && result.shipsToday;
      cutoffNode.hidden = !show;
      if (show) cutoffNode.textContent = data.templateCutoff.replace('TIME_TOKEN', formatRemaining(result.remainingMs, locale));
    }
    root.hidden = false;
  delete root.dataset.pending;
  };
  readState();
  render();
  const timer = setInterval(render, 60000);
  product?.addEventListener('nawiya:product:variant-changed', (event) => {
    purchasable = Boolean(event.detail?.available) && !event.detail?.preorder;
    render();
  });
  document.addEventListener('shopify:section:unload', (event) => { if (event.target.contains(root)) clearInterval(timer); });
}

function mountAll(scope = document) { scope.querySelectorAll?.('[data-delivery-estimate]').forEach(mount); }
if (typeof document !== 'undefined') {
  mountAll();
  document.addEventListener('shopify:section:load', (event) => mountAll(event.target));
}
